import { Injectable } from '@angular/core';
import { HttpClient, HttpContext, HttpParams } from '@angular/common/http';
import { HttpContextSkippingErrorToast, HttpContextSkippingSpinner } from 'app/services/auth/custom-http-interceptor.service';
import { Observable, of } from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';
import { environment } from 'environments/environment';
import { BaseResultModel } from 'app/models';

export interface RobertStatus {
    enabled: boolean;
    hasCredentials: boolean;
    // Live auth state of the CURRENT user's stored login (null = not checked: integration off / no creds).
    authOk?: boolean | null;
    // User-facing reason when authOk is false (Robert's own refusal text, or "could not reach Robert").
    authError?: string | null;
}

export interface RobertMyConfig {
    login?: string;
    baseUrl?: string;
    hasPassword?: boolean;
}

export interface RobertConnectionResult {
    success: boolean;
    message?: string;
    companyName?: string;
    userEmail?: string;
}

export interface RobertProject {
    id?: string;
    name?: string;
    clientName?: string;
    status?: string;
}

export interface RobertLinguistOption {
    id?: string;
    label?: string;
    email?: string;
    active?: boolean;
}

export interface RobertMatchResult {
    matched: boolean;
    robertAccountId?: string;
    name?: string;
    active?: boolean;
    message?: string;
}

export interface RobertAssignResult {
    success: boolean;
    message?: string;
}

// --- v2 / PR3a: mapping-backbone dropdown options ---
export interface RobertClientOption {
    id?: string;
    name?: string;
    email?: string;
    active?: boolean;
}

export interface RobertLanguageOption {
    code?: string;
    name?: string;
}

export interface RobertWorkflowOption {
    id?: string;
    name?: string;
    steps?: string[];
}

// One Robert workflow template + its TP mapping coverage — the workflow-dropdown "Robert workflows" section.
// A step is covered only by a ticked+mapped service (same pair the setup step-set derivation uses), so a row
// with no unmappedStepIds always reproduces a matching step selection when clicked.
export interface RobertWorkflowAvailability {
    id?: string;
    name?: string;
    steps?: string[];
    mappedServiceIds?: number[];
    unmappedStepIds?: string[];
    // Stamped only when getWorkflows was called with a projectId that is linked + snapshotted: the project's
    // workflow is frozen in Robert, so isProjectWorkflow marks the one live row and lockedByProject grays the rest.
    isProjectWorkflow?: boolean;
    lockedByProject?: boolean;
}

// Static Robert step catalog (no dedicated Robert endpoint exists — ids are stable/global). Shared by the
// service mapping dropdown (options) and the workflow-dropdown "Robert workflows" section (display names).
export const ROBERT_STEP_CATALOG: { id: string; name: string }[] = [
    { id: '1', name: 'Translate (TRA)' },
    { id: '2', name: 'Review (REV)' },
    { id: '3', name: 'Proof Reading (PR)' },
    { id: '4', name: 'Machine Translation (MT)' },
    { id: '5', name: 'Post-editing (PE)' },
    { id: '12', name: 'Finalize' },
    { id: '15', name: 'AI' },
    { id: '16', name: 'AI-Review' },
    { id: '17', name: 'LQA' },
];

export function robertStepName(stepId: string): string {
    return ROBERT_STEP_CATALOG.find(s => s.id === stepId)?.name ?? `step ${stepId}`;
}

// Robert readiness checklist — mirrors TPModels.RobertIntegration.RobertChecklist.
export interface RobertChecklist {
    clientMapped: boolean;
    stepsTotal: number;
    stepsEligible: number;
    stepsMapped: number;
    // -1 in pre-save (param) mode — the FE uses its local file list there.
    sourceFileCount: number;
    fileSent: boolean;
    // The source language the linked Robert project is locked to (Project.RobertSourceLanguageId). Null when the
    // project isn't linked / was linked pre-snapshot. A job whose source differs can't go to Robert (single-source).
    lockedSourceLanguageId?: number;
    // "Covered by Robert workflow" row: Robert freezes a project's workflow at creation, so a step outside it
    // can never get a task. Compared against the workflow snapshot stored on the project at link time;
    // workflowKnown=false (not linked / linked pre-snapshot) hides the row.
    workflowKnown: boolean;
    workflowName?: string;
    stepsInWorkflow: number;
    uncoveredStepNames: string[];
    // Live "Robert workflow" match (PRE-LINK only — project not yet in Robert). From a live fetch + the same
    // matcher setup uses. Only meaningful when workflowMatchEvaluated is true (else the FE hides the row).
    workflowMatchEvaluated?: boolean;
    workflowMatched?: boolean;
    workflowMatchName?: string;
}

// --- v2 / PR3b: "Set up in Robert" ---
export interface RobertProjectSetupRequest {
    clientJobId?: number;
    sourceLang?: string;
    targetLang?: string;
    workflow?: string;
}

export interface RobertSetupResult {
    success: boolean;
    robertProjectId?: string;
    projectUrl?: string;
    message?: string;
    needsLanguages?: boolean;
    needsWorkflow?: boolean;
    workflows?: RobertWorkflowOption[];
    // The triggering job has no Robert-ticked workflow step — no project created; silent skip on the auto-chain.
    notEligible?: boolean;
}

// --- v2 / PR3c: "Robert Files" ---
export interface RobertClientJobFileOption {
    id: number;          // ClientJobFile PK — the value sent to /clientjob/{id}/file
    fileId?: string;     // S3 file id — used to pre-select the already-sent file
    fileName?: string;
}

export interface RobertClientJobFilesRow {
    clientJobId: number;
    sourceLanguage?: string;
    targetLanguage?: string;
    robertSentFileId?: string;
    files: RobertClientJobFileOption[];
}

export interface RobertTaskLinkResult {
    success: boolean;
    message?: string;
    // The Robert task now linked to the resource job (set on success).
    robertTaskId?: string;
}

export interface RobertSendResult {
    success: boolean;
    message?: string;
    taskCount?: number;
    mappedCount?: number;
    needsLanguages?: boolean;
    // The job has no Robert-ticked workflow step — the auto-send treats this as a silent skip, not an error.
    notEligible?: boolean;
    // The source file is already in Robert (implicit send refused server-side) — silent skip on the FE.
    alreadySent?: boolean;
    // The job's source language differs from the project's locked Robert source — the send is refused and the
    // job stays a normal TP job. Silent skip on the FE (the save-time modal already warned the PM).
    sourceMismatch?: boolean;
}

// --- v2 / PR4: analysis import ---
export interface RobertAnalyzeResult {
    success: boolean;
    analysisId?: string;
    message?: string;
}

// --- slice A: translated-file return ---
export interface RobertFetchResult {
    success: boolean;
    // A translated file was downloaded and ingested on THIS call.
    fetched?: boolean;
    // The job already has an imported translated file — DB-only no-op (the editor-open auto-fetch hot path).
    alreadyImported?: boolean;
    // The final Robert task isn't finished yet — silent on the auto path; force bypasses this gate.
    notFinished?: boolean;
    taskStatus?: string;
    fileName?: string;
    message?: string;
    // The ingested Target file in the clientJobFiles shape (kept loosely typed here to avoid coupling this
    // proxy to the client-job file models) — append it to the open editor's files list without a reload.
    file?: any;
}

export interface RobertAnalysisResult {
    ready: boolean;
    message?: string;
    // ResponseCatToolAnalysisResult (kept loosely typed here to avoid coupling this proxy to the cat-count models).
    analysis?: any;
    // The Robert analysis record the result came from (a re-run in Robert yields a new id).
    recordId?: string;
    // The tenant's "Robert" CatTool id (resolved by name server-side) — what a Robert-sourced cat-count is tagged with.
    catToolId?: number;
    // The analysis CSV was generated + stored on the job as a Reference file on THIS call (see file).
    fetched?: boolean;
    // The job already holds the CSV for this Robert record (editor-open check: DB-only, no Robert call).
    alreadyImported?: boolean;
    // The job's source file was never sent to Robert — silent on the editor-open check.
    notSent?: boolean;
    // The stored analysis CSV in the clientJobFiles shape — append it to the open editor's files list.
    file?: any;
}

// Read-only readiness of one client job's Robert CAT analysis (project-page "analysis ready" hint).
export interface RobertAnalysisReadinessRow {
    clientJobId: number;
    ready?: boolean;      // Robert finished the analysis
    running?: boolean;    // still computing
    imported?: boolean;   // the finished record is already stored on the job as the analysis CSV
    recordId?: string;
}

// --- slice C: Robert→TP status sync (the project-page poll) ---
export interface RobertStatusSyncRow {
    resourceJobId: number;
    robertTaskId?: string;
    // Raw Robert status from the bulk read (display-cased, e.g. "In Progress"); null/absent = task not
    // returned by Robert (deleted there).
    robertStatus?: string;
    // Status-only finished signal (COMPLETED) — the detail read stays the authority for the progress bar.
    finished?: boolean;
    // THIS sync changed the TP job's status (the FE refreshes the rows when any row has it).
    tpUpdated?: boolean;
    tpJobStatusId?: number;
}

// --- slice B: live task status/progress (read fresh from Robert at display time; nothing persisted) ---
export interface RobertTaskStatus {
    resourceJobId: number;
    robertTaskId?: string;
    // The task was actually read from Robert on this call — the fields below are only meaningful when true.
    found: boolean;
    // Raw Robert task status (e.g. Created / InProgress / Completed / Accepted / Split).
    status?: string;
    // The same finished OR-rule as the translated-file fetch gate.
    finished?: boolean;
    progressPercentage?: number;
    totalWords?: number;
    confirmedWords?: number;
}

/**
 * Proxy for the TP Robert integration endpoints (manual mode / v1). All calls are BaseResultModel-wrapped and
 * read fresh from Robert on the backend (no caching). Visibility of the consuming UI is gated by the company
 * Robert toggle (globalConfiguration.companySetting.robertIntegrationEnabled) + the PM portal.
 */
@Injectable({ providedIn: 'root' })
export class RobertService {
    private readonly uri = environment.apiUrl + '/robert';

    constructor(private http: HttpClient) {}

    getStatus(): Observable<BaseResultModel<RobertStatus>> {
        return this.http.get<BaseResultModel<RobertStatus>>(`${this.uri}/status`);
    }

    // --- Auth state for the "Robert login rejected" pill (job editors + project page). One background read
    // shared by every host for 30s (the backend caches the token / refusal too), silent on failure, never
    // flashes the global spinner. Null = unknown (request failed) — hosts show nothing in that case. ---
    private authState$: Observable<RobertStatus | null> | null = null;
    private authStateFetchedAt = 0;
    private static readonly AUTH_STATE_TTL_MS = 30_000;

    getAuthState(): Observable<RobertStatus | null> {
        const now = Date.now();
        if (!this.authState$ || now - this.authStateFetchedAt > RobertService.AUTH_STATE_TTL_MS) {
            this.authStateFetchedAt = now;
            this.authState$ = this.http.get<BaseResultModel<RobertStatus>>(`${this.uri}/status`, {
                context: new HttpContext().set(HttpContextSkippingSpinner, true).set(HttpContextSkippingErrorToast, true),
            }).pipe(
                map(res => (res.status ? res.response ?? null : null)),
                catchError(() => of(null)),
                shareReplay(1),
            );
        }
        return this.authState$;
    }

    // Call after the user's Robert login changes (My Profile save / test) so the next read is fresh.
    invalidateAuthState(): void {
        this.authState$ = null;
        this.authStateFetchedAt = 0;
    }

    // --- Per-user account (My Profile: connect / test the current user's own Robert account) ---
    getMyConfig(): Observable<BaseResultModel<RobertMyConfig>> {
        return this.http.get<BaseResultModel<RobertMyConfig>>(`${this.uri}/my-config`);
    }
    // Omitted fields fall back to the saved values on the backend (test stored creds, or freshly-typed ones).
    testConnection(login?: string, password?: string, baseUrl?: string): Observable<BaseResultModel<RobertConnectionResult>> {
        return this.http.post<BaseResultModel<RobertConnectionResult>>(`${this.uri}/test-connection`, { login, password, baseUrl });
    }

    // --- Resource editor "Robert" tab: a RESOURCE's linked user's Robert account, managed by a PM. Mirrors
    // the my-config / test-connection pair (those act on the CURRENT user only) keyed by resource id, plus a
    // dedicated save because the resource form save has no user fold-in like the profile save does. ---

    // Null response = the resource has no linked portal user.
    getResourceConfig(resourceId: number): Observable<BaseResultModel<RobertMyConfig>> {
        return this.http.get<BaseResultModel<RobertMyConfig>>(`${this.uri}/resource/${resourceId}/config`);
    }

    // Blank/omitted fields preserve the stored values on the backend (the password is write-only).
    setResourceConfig(resourceId: number, login?: string, password?: string): Observable<BaseResultModel<boolean>> {
        return this.http.put<BaseResultModel<boolean>>(`${this.uri}/resource/${resourceId}/config`, { login, password });
    }

    // Omitted fields fall back to the resource's linked user's saved values.
    testResourceConnection(resourceId: number, login?: string, password?: string): Observable<BaseResultModel<RobertConnectionResult>> {
        return this.http.post<BaseResultModel<RobertConnectionResult>>(`${this.uri}/resource/${resourceId}/test-connection`, { login, password });
    }

    getProjects(): Observable<BaseResultModel<RobertProject[]>> {
        return this.http.get<BaseResultModel<RobertProject[]>>(`${this.uri}/projects`);
    }

    getLinguists(): Observable<BaseResultModel<RobertLinguistOption[]>> {
        return this.http.get<BaseResultModel<RobertLinguistOption[]>>(`${this.uri}/linguists`);
    }

    getProjectLink(projectId: number): Observable<BaseResultModel<string>> {
        return this.http.get<BaseResultModel<string>>(`${this.uri}/project/${projectId}/link`);
    }

    linkProject(projectId: number, robertProjectId: string | null): Observable<BaseResultModel<boolean>> {
        return this.http.put<BaseResultModel<boolean>>(`${this.uri}/project/${projectId}/link`, { robertProjectId });
    }

    // The task auto-mapped to a resource job (re-read after a file send so the editor can enable Assign).
    getResourceJobMap(resourceJobId: number): Observable<BaseResultModel<string>> {
        return this.http.get<BaseResultModel<string>>(`${this.uri}/resourcejob/${resourceJobId}/map`);
    }

    // Create/link the Robert task for one resource job's step — the recovery for a step added after the client
    // job's file was already sent (the send is what spawns tasks, so late-added steps have none).
    createResourceJobTask(resourceJobId: number): Observable<BaseResultModel<RobertTaskLinkResult>> {
        return this.http.post<BaseResultModel<RobertTaskLinkResult>>(`${this.uri}/resourcejob/${resourceJobId}/task`, {});
    }

    getResourceMatch(resourceId: number): Observable<BaseResultModel<RobertMatchResult>> {
        return this.http.get<BaseResultModel<RobertMatchResult>>(`${this.uri}/resource/${resourceId}/match`);
    }

    assignResourceJob(resourceJobId: number): Observable<BaseResultModel<RobertAssignResult>> {
        return this.http.post<BaseResultModel<RobertAssignResult>>(`${this.uri}/resourcejob/${resourceJobId}/assign`, {});
    }

    // --- v2 / PR3a: mapping backbone. Lookup lists feed the 4 admin mapping dropdowns; per-entity match
    // resolves stored id -> auto-suggest; per-entity set persists an admin pick (dedicated endpoint, so an
    // entity's own save never touches / wipes the Robert id). ---

    getClients(): Observable<BaseResultModel<RobertClientOption[]>> {
        return this.http.get<BaseResultModel<RobertClientOption[]>>(`${this.uri}/clients`);
    }

    getLanguages(): Observable<BaseResultModel<RobertLanguageOption[]>> {
        return this.http.get<BaseResultModel<RobertLanguageOption[]>>(`${this.uri}/languages`);
    }

    getClientMatch(clientId: number): Observable<BaseResultModel<RobertMatchResult>> {
        return this.http.get<BaseResultModel<RobertMatchResult>>(`${this.uri}/client/${clientId}/match`);
    }

    setClient(clientId: number, robertClientId: string | null): Observable<BaseResultModel<boolean>> {
        return this.http.put<BaseResultModel<boolean>>(`${this.uri}/client/${clientId}/map`, { robertClientId });
    }

    // Ids of clients with a persisted Robert mapping (Client.RobertClientId) — powers the per-option Robert
    // markers in client dropdowns. DB-only; response is null when the company Robert toggle is off.
    getMappedClientIds(): Observable<BaseResultModel<number[]>> {
        return this.http.get<BaseResultModel<number[]>>(`${this.uri}/client/mapped-ids`);
    }

    // Ids of resources with a persisted Robert mapping (Resource.RobertAccountId) — powers the per-option
    // Robert markers in the resource dropdown. DB-only; response is null when the company Robert toggle is off.
    getMappedResourceIds(): Observable<BaseResultModel<number[]>> {
        return this.http.get<BaseResultModel<number[]>>(`${this.uri}/resource/mapped-ids`);
    }

    getLanguageMatch(languageId: number): Observable<BaseResultModel<RobertMatchResult>> {
        return this.http.get<BaseResultModel<RobertMatchResult>>(`${this.uri}/language/${languageId}/match`);
    }

    // Ids of languages with a persisted Robert mapping (Language.RobertLanguageCode) — powers the per-option
    // Robert markers in source/target language dropdowns. DB-only; response is null when the company Robert toggle is off.
    getMappedLanguageIds(): Observable<BaseResultModel<number[]>> {
        return this.http.get<BaseResultModel<number[]>>(`${this.uri}/language/mapped-ids`);
    }

    setLanguage(languageId: number, robertLanguageCode: string | null): Observable<BaseResultModel<boolean>> {
        return this.http.put<BaseResultModel<boolean>>(`${this.uri}/language/${languageId}/map`, { robertLanguageCode });
    }

    getServiceStep(serviceId: number): Observable<BaseResultModel<string>> {
        return this.http.get<BaseResultModel<string>>(`${this.uri}/service/${serviceId}/step`);
    }

    setServiceStep(serviceId: number, robertStepId: string | null): Observable<BaseResultModel<boolean>> {
        return this.http.put<BaseResultModel<boolean>>(`${this.uri}/service/${serviceId}/step`, { robertStepId });
    }

    // --- v2 / PR3b: create the Robert project from TP data (stores Project.RobertProjectId). Pass overrides
    // (workflow / source language) on a retry when the backend returns needsWorkflow / needsLanguages. ---
    // opts.silent (the client-job save auto-chain): skip the interceptor's error toast + spinner — a failed
    // background setup of a project that isn't in Robert yet must not surface as an error on a plain job save.
    setupProject(projectId: number, request?: RobertProjectSetupRequest, opts?: { silent?: boolean }): Observable<BaseResultModel<RobertSetupResult>> {
        const context = opts?.silent
            ? new HttpContext().set(HttpContextSkippingErrorToast, true).set(HttpContextSkippingSpinner, true)
            : undefined;
        return this.http.post<BaseResultModel<RobertSetupResult>>(`${this.uri}/project/${projectId}/setup`, request ?? {}, { context });
    }

    // Robert readiness checklist (client mapped / steps mapped / source-file state). DB-only. Pass clientJobId
    // for a SAVED job (authoritative — steps/files derived from its rows) or clientId + serviceIds pre-save.
    // projectId (pre-save mode only) enables the workflow-coverage fields against the linked Robert project.
    // response is null when the company Robert toggle is off. stepsEligible also powers the save-time warning.
    getChecklist(req: { clientJobId?: number; clientId?: number; serviceIds?: number[]; projectId?: number }): Observable<BaseResultModel<RobertChecklist>> {
        let params = new HttpParams();
        if (req.clientJobId) params = params.set('clientJobId', String(req.clientJobId));
        if (req.clientId) params = params.set('clientId', String(req.clientId));
        if (req.serviceIds?.length) params = params.set('ids', req.serviceIds.join(','));
        if (req.projectId) params = params.set('projectId', String(req.projectId));
        return this.http.get<BaseResultModel<RobertChecklist>>(`${this.uri}/checklist`, { params });
    }

    // Ids of services ticked for Robert (Service.UseInRobertIntegration) — powers the per-step Robert markers
    // in the workflow-steps dropdown. DB-only; response is null when the company Robert toggle is off.
    getEligibleServiceIds(): Observable<BaseResultModel<number[]>> {
        return this.http.get<BaseResultModel<number[]>>(`${this.uri}/service/eligible-ids`);
    }

    // Robert workflow templates + per-step TP coverage — the workflow-dropdown "Robert workflows" section.
    // Pass projectId to stamp the linked project's frozen-workflow lock on the rows (isProjectWorkflow /
    // lockedByProject). Live Robert read; response is null when the toggle is off / creds missing / Robert down.
    getWorkflows(projectId?: number): Observable<BaseResultModel<RobertWorkflowAvailability[]>> {
        let params = new HttpParams();
        if (projectId) params = params.set('projectId', String(projectId));
        return this.http.get<BaseResultModel<RobertWorkflowAvailability[]>>(`${this.uri}/workflows`, { params });
    }

    // --- v2 / PR3c: the "Robert Files" window — list a project's client-job source files, and send one. ---
    getProjectRobertFiles(projectId: number): Observable<BaseResultModel<RobertClientJobFilesRow[]>> {
        return this.http.get<BaseResultModel<RobertClientJobFilesRow[]>>(`${this.uri}/project/${projectId}/robert-files`);
    }

    sendClientJobFile(clientJobId: number, fileId?: number): Observable<BaseResultModel<RobertSendResult>> {
        return this.http.post<BaseResultModel<RobertSendResult>>(`${this.uri}/clientjob/${clientJobId}/file`, { fileId });
    }

    // --- slice A: fetch the translated (target) file back from Robert and ingest it as a TP Target file.
    // force=false is the editor-open auto-fetch (already-imported and not-finished are silent server-side
    // no-ops); force=true is the PM's manual re-fetch, which deliberately bypasses both gates.
    // status:false here is an EXPECTED state (task not finished yet), not an error — skip the interceptor's
    // global red toast; both callers render the outcome themselves (auto = silent, manual = its own message).
    fetchTranslation(clientJobId: number, force = false): Observable<BaseResultModel<RobertFetchResult>> {
        return this.http.post<BaseResultModel<RobertFetchResult>>(`${this.uri}/clientjob/${clientJobId}/fetch-translation`, { force },
            { context: new HttpContext().set(HttpContextSkippingErrorToast, true) });
    }

    // Project-page "analysis ready" hint: readiness for every client job whose file is in Robert — ONE Robert
    // call per project, nothing stored. Background read (page open + slow poll while pending): no spinner, no
    // red toast on failure; null response = gate off / project unlinked / Robert unreachable.
    getProjectAnalysisReadiness(projectId: number): Observable<BaseResultModel<RobertAnalysisReadinessRow[]>> {
        return this.http.get<BaseResultModel<RobertAnalysisReadinessRow[]>>(`${this.uri}/project/${projectId}/analysis-readiness`,
            { context: new HttpContext().set(HttpContextSkippingErrorToast, true).set(HttpContextSkippingSpinner, true) });
    }

    // --- v2 / PR4: trigger a Robert CAT analysis, then poll until the result is ready. ---
    analyzeClientJob(clientJobId: number): Observable<BaseResultModel<RobertAnalyzeResult>> {
        return this.http.post<BaseResultModel<RobertAnalyzeResult>>(`${this.uri}/clientjob/${clientJobId}/analyze`, {});
    }

    getClientJobAnalysis(clientJobId: number): Observable<BaseResultModel<RobertAnalysisResult>> {
        return this.http.get<BaseResultModel<RobertAnalysisResult>>(`${this.uri}/clientjob/${clientJobId}/analysis`);
    }

    // Client-job-level analysis sync (the CAT Count modal's "Robert" analysis type + the editor-open readiness
    // check). When Robert has finished, the backend stores the bands as a CSV Reference file on the job (once per
    // Robert record; a re-run replaces it) and returns the computed result for the review modal. Pricing is only
    // persisted by the PM's Apply. force=false (editor open) is a DB-only no-op once the CSV exists and must stay
    // silent (no spinner / no red toast: "still running" is an expected state); force=true = the modal's Analyse.
    syncClientJobAnalysis(clientJobId: number, force: boolean): Observable<BaseResultModel<RobertAnalysisResult>> {
        const context = force
            ? new HttpContext().set(HttpContextSkippingErrorToast, true)
            : new HttpContext().set(HttpContextSkippingErrorToast, true).set(HttpContextSkippingSpinner, true);
        return this.http.post<BaseResultModel<RobertAnalysisResult>>(`${this.uri}/clientjob/${clientJobId}/analysis-sync`, { force }, { context });
    }

    // --- slice B: live task status/progress. Read-only; both callers stay silent on failure. All three
    // status calls skip the global blocking spinner — they run in the background (page open / 6s poll /
    // 30s detail refresh) and must never flash the app-wide overlay.

    // One resource job's Robert task status (the editor status chip). Null response = gate off / project
    // not linked / no mapped task.
    getResourceJobTaskStatus(resourceJobId: number): Observable<BaseResultModel<RobertTaskStatus>> {
        return this.http.get<BaseResultModel<RobertTaskStatus>>(`${this.uri}/resourcejob/${resourceJobId}/task-status`,
            { context: new HttpContext().set(HttpContextSkippingSpinner, true) });
    }

    // Statuses for ALL of a project's task-mapped resource jobs — fetched when the project page opens and
    // on the slow (~30s) poll refresh of the progress bars.
    getProjectTaskStatuses(projectId: number): Observable<BaseResultModel<RobertTaskStatus[]>> {
        return this.http.get<BaseResultModel<RobertTaskStatus[]>>(`${this.uri}/project/${projectId}/task-status`,
            { context: new HttpContext().set(HttpContextSkippingSpinner, true) });
    }

    // Statuses for ONE client job's task-mapped resource jobs — fetched lazily when the PM expands that
    // client job's parent row on the project page (the project-wide read above stays for expand-all).
    getClientJobTaskStatuses(clientJobId: number): Observable<BaseResultModel<RobertTaskStatus[]>> {
        return this.http.get<BaseResultModel<RobertTaskStatus[]>>(`${this.uri}/clientjob/${clientJobId}/task-status`,
            { context: new HttpContext().set(HttpContextSkippingSpinner, true) });
    }

    // --- slice C: Robert→TP status sync, polled every ~6s while the project page is open. ONE bulk Robert
    // call server-side; mapped status changes are mirrored onto the TP jobs. Poll failures must stay silent
    // (no red toast every tick) and the poll must not flash the global spinner — hence both opt-out tokens.
    syncProjectTaskStatuses(projectId: number): Observable<BaseResultModel<RobertStatusSyncRow[]>> {
        return this.http.post<BaseResultModel<RobertStatusSyncRow[]>>(`${this.uri}/project/${projectId}/status-sync`, {},
            { context: new HttpContext().set(HttpContextSkippingErrorToast, true).set(HttpContextSkippingSpinner, true) });
    }
}
